import { supabase } from './supabase';
import { inlineCodexReference } from './codexText';
import { isStudioReferenceUrl, resolveReferenceImageUrl } from './referenceImageUrl';
import { preserveProtectedClothingPixels } from './clothingMask';

async function clothingResponse(body: Record<string, string>): Promise<Response> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Sign in to change clothing.');
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/edit-clothing`, {
        method: 'POST', signal: AbortSignal.timeout(35_000),
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}`, apikey: import.meta.env.VITE_SUPABASE_ANON_KEY },
        body: JSON.stringify(body)
    });
    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const error = new Error(typeof data.error === 'string' ? data.error : 'Could not change clothing. Your body image has been kept.');
        Object.assign(error, { status: response.status });
        throw error;
    }
    return response;
}

async function clothingRequest(body: Record<string, string>): Promise<Blob> {
    return (await clothingResponse(body)).blob();
}

type PendingClothingEdit = { id: string; mask: string; expires: number };

async function pendingKey(image: string): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Sign in to change clothing.');
    const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(image)));
    return `ezart-clothing:${session.user.id}:${[...digest].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

function pendingEdit(key: string): PendingClothingEdit | null {
    try {
        const value = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (value && typeof value.id === 'string' && typeof value.mask === 'string' && value.expires > Date.now()) return value;
    } catch { /* Storage can be unavailable; server tracking still prevents duplicate charges. */ }
    return null;
}

export async function pendingClothingMask(image: string): Promise<string | null> {
    return pendingEdit(await pendingKey(image))?.mask || null;
}

export async function acknowledgeClothingEdit(image: string): Promise<void> {
    try { sessionStorage.removeItem(await pendingKey(image)); } catch { /* Upload succeeded; acknowledgment is best effort. */ }
}

async function queuedClothingEdit(image: string, mask: string, onStatus?: (message: string) => void): Promise<{ blob: Blob; mask: string }> {
    const key = await pendingKey(image);
    const pending = pendingEdit(key);
    const jobId = pending?.id || crypto.randomUUID();
    mask = pending?.mask || mask;
    // A retry keeps its ID so it cannot reserve another credit or start another generation.
    const request = { operation: 'edit', job_id: jobId, image, mask };
    for (let attempt = 0; !pending; attempt++) {
        try {
            await clothingResponse(request);
            try { sessionStorage.setItem(key, JSON.stringify({ id: jobId, mask, expires: Date.now() + 31 * 60_000 })); } catch { /* Best effort. */ }
            break;
        }
        catch (error) {
            const status = (error as { status?: number }).status;
            if (attempt || status && status < 500) throw error;
        }
    }
    const deadline = Date.now() + 31 * 60_000;
    while (Date.now() < deadline) {
        await new Promise(resolve => setTimeout(resolve, 2500));
        try {
            const job = await (await clothingResponse({ operation: 'status', job_id: jobId })).json();
            if (job.state === 'succeeded') {
                const blob = await clothingRequest({ operation: 'result', job_id: jobId });
                return { blob, mask };
            }
            onStatus?.(job.state === 'queued' ? 'Waiting for the image editor…' : 'Changing clothing…');
        } catch (error) {
            if ((error as { status?: number }).status !== 503 && !(error instanceof DOMException) && !(error instanceof TypeError)) {
                try { sessionStorage.removeItem(key); } catch { /* Best effort. */ }
                throw error;
            }
        }
    }
    throw new Error('The clothing edit is still unavailable. Your original image has been kept.');
}

export async function loadClothingSource(value: string): Promise<string> {
    if (!isStudioReferenceUrl(value)) return inlineCodexReference(value);
    const blob = await clothingRequest({ operation: 'source', image: resolveReferenceImageUrl(value) });
    const url = URL.createObjectURL(blob);
    try { return await inlineCodexReference(url); }
    finally { URL.revokeObjectURL(url); }
}

export function loadCanvasImage(source: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Could not open the body image.'));
        image.src = source;
    });
}

export async function replaceClothing(image: string, mask: HTMLCanvasElement, onStatus?: (message: string) => void): Promise<Blob> {
    let pixels = mask.getContext('2d', { willReadFrequently: true })!.getImageData(0, 0, mask.width, mask.height).data;
    if (!pixels.some((v, i) => i % 4 === 0 && v > 0)) throw new Error('Select or paint a clothing area first.');
    const original = await loadCanvasImage(image);
    const result = await queuedClothingEdit(image, mask.toDataURL('image/png'), onStatus);
    const savedMask = await loadCanvasImage(result.mask);
    mask.getContext('2d', { willReadFrequently: true })!.drawImage(savedMask, 0, 0, mask.width, mask.height);
    pixels = mask.getContext('2d', { willReadFrequently: true })!.getImageData(0, 0, mask.width, mask.height).data;
    const editedUrl = URL.createObjectURL(result.blob);
    try {
        const edited = await loadCanvasImage(editedUrl);
        if (Math.abs(edited.naturalWidth / edited.naturalHeight - original.naturalWidth / original.naturalHeight) > 0.02) throw new Error('The clothing edit changed the image proportions. Your original image has been kept.');
        const canvas = document.createElement('canvas');
        canvas.width = mask.width; canvas.height = mask.height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
        ctx.drawImage(original, 0, 0, canvas.width, canvas.height);
        const sourcePixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(edited, 0, 0, canvas.width, canvas.height);
        const editPixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
        sourcePixels.data.set(preserveProtectedClothingPixels(sourcePixels.data, editPixels.data, pixels));
        ctx.putImageData(sourcePixels, 0, 0);
        return await new Promise<Blob>((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Could not save the clothing edit.')), 'image/png'));
    } finally { URL.revokeObjectURL(editedUrl); }
}
